import {
  useState,
  useMemo,
  useCallback,
  useEffect,
  type CSSProperties,
} from "react";
import { MILESTONES, CREATINE_TIPS, SUPPLEMENT, type Milestone } from "./data";
import {
  DEFAULT_DATA,
  loadState,
  recordCheckin,
  markStartedTracking,
  resetAll,
  userId,
  type DateStr,
  type StreakData,
} from "./persistence";
import StreakSkeleton, { type SkeletonLayout } from "./skeleton";
import WeekStrip, {
  type WeekDay,
  type WeekDayStatus,
  type WeekStripStyles,
} from "./week-strip";
import { track } from "../mixpanel";

// ── Dev Mode ───────────────────────────────────────────────────────

const params = new URLSearchParams(window.location.search);
const DEV_MODE = params.get("dev") === "1";
const DEV_START_DATE = params.get("date"); // YYYY-MM-DD

function getInitialDevDate(): Date | null {
  if (!DEV_MODE) return null;
  if (DEV_START_DATE) {
    const d = new Date(DEV_START_DATE + "T00:00:00");
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
}

// ── Utility Functions ──────────────────────────────────────────────

function dateStr(d: Date): DateStr {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function todayStr(now: Date): DateStr {
  return dateStr(now);
}

const WEEKDAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTH_LABELS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateLabel(d: DateStr): string {
  const date = new Date(d + "T00:00:00");
  return `${WEEKDAY_LABELS[date.getDay()]}, ${MONTH_LABELS[date.getMonth()]} ${date.getDate()}`;
}

function yesterdayStr(now: Date): DateStr {
  const d = new Date(now);
  d.setDate(d.getDate() - 1);
  return dateStr(d);
}

function getDayOfYear(now: Date): number {
  const start = new Date(now.getFullYear(), 0, 0);
  return Math.floor((now.getTime() - start.getTime()) / 86400000);
}

function calcStreak(checkins: DateStr[], now: Date): number {
  if (!checkins.length) return 0;
  const sorted = [...checkins].sort();
  const today = todayStr(now);
  const yesterday = yesterdayStr(now);
  const last = sorted[sorted.length - 1];
  if (last !== today && last !== yesterday) return 0;
  let count = 1;
  for (let i = sorted.length - 2; i >= 0; i--) {
    const curr = new Date(sorted[i + 1] + "T00:00:00");
    const prev = new Date(sorted[i] + "T00:00:00");
    const diff = (curr.getTime() - prev.getTime()) / 86400000;
    if (diff === 1) count++;
    else break;
  }
  return count;
}

function calcPreviousStreak(checkins: DateStr[], now: Date): number {
  if (!checkins.length) return 0;
  const sorted = [...checkins].sort();
  const today = todayStr(now);
  const yesterday = yesterdayStr(now);
  const last = sorted[sorted.length - 1];
  if (last === today || last === yesterday) return 0;
  let count = 1;
  for (let i = sorted.length - 2; i >= 0; i--) {
    const curr = new Date(sorted[i + 1] + "T00:00:00");
    const prev = new Date(sorted[i] + "T00:00:00");
    const diff = (curr.getTime() - prev.getTime()) / 86400000;
    if (diff === 1) count++;
    else break;
  }
  return count;
}

function calcMonthlyRate(checkins: DateStr[], now: Date): number | null {
  const year = now.getFullYear();
  const month = now.getMonth();
  const dayOfMonth = now.getDate();
  const thisMonth = checkins.filter((d) => {
    const dt = new Date(d + "T00:00:00");
    return dt.getFullYear() === year && dt.getMonth() === month;
  });
  if (dayOfMonth === 0 || thisMonth.length === 0) return null;
  return Math.round((thisMonth.length / dayOfMonth) * 100);
}

function getWeekDays(checkins: DateStr[], now: Date): WeekDay[] {
  const checkinSet = new Set(checkins);
  const days: WeekDay[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setDate(d.getDate() - i);
    const ds = dateStr(d);
    const isToday = i === 0;
    let status: WeekDayStatus;
    if (isToday && checkinSet.has(ds)) status = "today-checked";
    else if (isToday) status = "today";
    else if (checkinSet.has(ds)) status = "checked";
    else status = "missed";
    days.push({
      dayName: WEEKDAY_LABELS[d.getDay()],
      dayNum: d.getDate(),
      status,
      dateStr: ds,
    });
  }
  return days;
}

function getNextMilestone(streak: number): Milestone | null {
  return MILESTONES.find((m) => m.days > streak) ?? null;
}

// ── Styles ─────────────────────────────────────────────────────────

const s: Record<string, CSSProperties> = {
  wrap: {
    fontFamily: "'Inter', -apple-system, sans-serif",
    maxWidth: 420,
    margin: "0 auto",
    background: "#FFF",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    color: "#1A1A1A",
    position: "relative",
  },

  // Header
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "8px 20px 10px",
    minHeight: 44,
  },
  headerLeft: { display: "flex", alignItems: "center", gap: 10 },
  backArrow: {
    fontSize: 22,
    cursor: "pointer",
    padding: "8px 4px",
    background: "none",
    border: "none",
    color: "inherit",
  },
  headerTitle: { fontSize: 17, fontWeight: 700, letterSpacing: -0.3 },

  // Hero
  hero: {
    background: "#000",
    padding: "16px 20px 14px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    position: "relative",
    overflow: "hidden",
  },
  heroGlow: {
    position: "absolute",
    right: -30,
    top: -30,
    width: 140,
    height: 140,
    background:
      "radial-gradient(circle, rgba(4,65,190,0.12) 0%, transparent 70%)",
    pointerEvents: "none",
  },
  heroNumberBlock: { textAlign: "center", minWidth: 80, position: "relative" },
  heroCount: {
    fontSize: 46,
    fontWeight: 900,
    color: "#FFF",
    lineHeight: 1,
    letterSpacing: -2,
  },
  heroUnit: {
    fontSize: 10,
    fontWeight: 700,
    color: "rgba(255,255,255,0.4)",
    letterSpacing: 2,
    textTransform: "uppercase",
    marginTop: 2,
  },
  heroInfo: { flex: 1, position: "relative" },
  heroProduct: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1.5,
    textTransform: "uppercase",
    color: "#6B8FD4",
    marginBottom: 4,
  },
  heroMessage: {
    fontSize: 13,
    color: "rgba(255,255,255,0.7)",
    lineHeight: 1.4,
  },

  // Stats Row
  statsRow: { display: "flex", borderBottom: "1px solid #F0F0F0" },
  statItem: {
    flex: 1,
    textAlign: "center",
    padding: "10px 6px",
    borderRight: "1px solid #F0F0F0",
  },
  statItemLast: { flex: 1, textAlign: "center", padding: "10px 6px" },
  statValue: { fontSize: 18, fontWeight: 800, letterSpacing: -0.5 },
  statLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "#888",
    letterSpacing: 0.5,
    textTransform: "uppercase",
    marginTop: 1,
  },

  // Week Strip
  weekStrip: { padding: "14px 20px 10px" },
  weekStripHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    marginBottom: 10,
  },
  weekStripLabel: { fontSize: 11, fontWeight: 700, letterSpacing: -0.2 },
  weekStripGoal: { fontSize: 11, fontWeight: 600, color: "#0441BE" },
  weekDays: { display: "flex", justifyContent: "space-between", gap: 6 },
  weekDay: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    flex: 1,
  },
  weekDayName: {
    fontSize: 10,
    fontWeight: 600,
    color: "#B0B0B0",
    letterSpacing: 0.3,
    textTransform: "uppercase",
  },
  weekCircle: {
    width: 38,
    height: 38,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 12,
    fontWeight: 600,
    color: "#1A1A1A",
    background: "#F5F5F5",
    position: "relative",
  },
  weekCircleChecked: { background: "#0441BE", color: "#FFF", fontWeight: 700 },
  weekCircleToday: {
    border: "2.5px solid #0441BE",
    color: "#0441BE",
    fontWeight: 700,
    background: "#FFF",
  },
  weekCircleTodayChecked: {
    background: "#0441BE",
    color: "#FFF",
    borderColor: "#FFF",
    boxShadow: "0 0 0 2.5px #0441BE",
  },
  weekCircleMissed: { background: "#F5F5F5", color: "#D0D0D0" },
  weekCircleSelected: {
    border: "2.5px solid #0441BE",
    background: "#FFF",
    color: "#0441BE",
    fontWeight: 700,
  },
  missedStrike: {
    position: "absolute",
    width: 20,
    height: 1.5,
    background: "#E0D0D0",
    transform: "rotate(-45deg)",
  },

  // Welcome Back Card
  welcomeCard: {
    margin: "12px 20px",
    padding: "14px 16px",
    background: "#FFF9F0",
    border: "1px solid #F0E4D0",
    borderRadius: 10,
  },
  wbTitle: { fontSize: 13, fontWeight: 700, marginBottom: 3 },
  wbDesc: { fontSize: 12, color: "#777", lineHeight: 1.5 },
  wbPrevStreak: {
    fontSize: 11,
    color: "#B08840",
    fontWeight: 600,
    marginTop: 6,
  },

  // Tip Card
  tipCard: {
    margin: "0 20px 10px",
    padding: "10px 14px",
    background: "#F8F9FC",
    border: "1px solid #E8ECF4",
    borderRadius: 10,
  },
  tipHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 4,
  },
  tipLabel: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 1,
    textTransform: "uppercase",
    color: "#0441BE",
  },
  tipCounter: { fontSize: 10, color: "#B0B0B0", fontWeight: 500 },
  tipText: { fontSize: 12, color: "#444", lineHeight: 1.5 },
  tipSource: { fontSize: 9, color: "#B0B0B0", marginTop: 4 },

  // Milestones
  milestonesSection: { padding: "2px 20px 8px" },
  sectionTitleSm: {
    fontSize: 12,
    fontWeight: 700,
    color: "#1A1A1A",
    marginBottom: 10,
    letterSpacing: -0.2,
  },
  milestoneTrack: {
    display: "flex",
    gap: 8,
    overflowX: "auto",
    paddingBottom: 4,
  },
  milestone: {
    minWidth: 68,
    padding: "10px 8px",
    background: "#F7F7F7",
    borderRadius: 10,
    textAlign: "center",
    flexShrink: 0,
  },
  msAchieved: { background: "#000", color: "#FFF" },
  msNext: { background: "#FFF", border: "1.5px dashed #0441BE" },
  msIcon: { fontSize: 16, marginBottom: 2 },
  msDays: { fontSize: 15, fontWeight: 800, letterSpacing: -0.5 },
  msDaysBlue: { color: "#0441BE" },
  msLabel: {
    fontSize: 10,
    fontWeight: 600,
    color: "#888",
    letterSpacing: 0.3,
    textTransform: "uppercase",
    marginTop: 1,
  },
  msLabelAchieved: { color: "rgba(255,255,255,0.5)" },

  // Progress Bar
  progressBar: { padding: "0 20px 12px" },
  mpBarWrap: { display: "flex", alignItems: "center", gap: 10 },
  mpBar: {
    flex: 1,
    height: 8,
    background: "#F0F0F0",
    borderRadius: 4,
    overflow: "hidden",
  },
  mpBarFill: { height: "100%", borderRadius: 4, background: "#0441BE" },
  mpBarLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#888",
    whiteSpace: "nowrap",
  },
  mpText: { fontSize: 11, color: "#888", marginTop: 6 },

  // Onboarding
  onboardingContent: { padding: "30px 24px", textAlign: "center" },
  onboardingTitle: {
    fontSize: 18,
    fontWeight: 800,
    letterSpacing: -0.3,
    marginBottom: 8,
  },
  onboardingDesc: {
    fontSize: 13,
    color: "#777",
    lineHeight: 1.6,
    marginBottom: 24,
  },
  onboardingSteps: { textAlign: "left", padding: "0 8px" },
  onboardingStep: {
    display: "flex",
    gap: 12,
    marginBottom: 18,
    alignItems: "flex-start",
  },
  stepNum: {
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: "#F0F0F0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 800,
    flexShrink: 0,
    color: "#666",
  },
  stepText: { fontSize: 13, lineHeight: 1.5, color: "#555" },

  // Sticky CTA Footer
  stickyFooter: {
    padding: "12px 20px 20px",
    background: "#FFF",
    borderTop: "1px solid #F0F0F0",
    marginTop: "auto",
  },
  ctaBtn: {
    width: "100%",
    padding: 15,
    background: "#000",
    color: "#FFF",
    border: "none",
    borderRadius: 12,
    fontSize: 15,
    fontWeight: 700,
    letterSpacing: 0.2,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 8,
    fontFamily: "inherit",
    minHeight: 50,
  },
  ctaDone: { background: "#0441BE", opacity: 0.85, cursor: "default" },
  ctaStart: { background: "#0441BE" },
  fda: {
    padding: "6px 0 4px",
    fontSize: 9,
    color: "#C0C0C0",
    lineHeight: 1.4,
    textAlign: "center",
  },

  // Content area
  content: { flex: 1, overflowY: "auto" },

  // Dev Bar
  devBar: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    background: "#1a1a1a",
    color: "#FFF",
    fontSize: 11,
    fontFamily: "monospace",
    flexWrap: "wrap",
  },
  devLabel: {
    background: "#FF3B30",
    color: "#FFF",
    padding: "1px 5px",
    borderRadius: 3,
    fontSize: 9,
    fontWeight: 700,
    letterSpacing: 1,
  },
  devDate: { fontWeight: 600 },
  devScreen: { color: "#999", marginRight: "auto" },
  devBtn: {
    background: "#333",
    color: "#FFF",
    border: "none",
    borderRadius: 4,
    padding: "3px 8px",
    fontSize: 10,
    fontWeight: 600,
    cursor: "pointer",
    fontFamily: "inherit",
  },
  devBtnDanger: { background: "#8B0000" },
};

// ── Component ──────────────────────────────────────────────────────

export default function StreakTracker() {
  const [data, setData] = useState<StreakData>(DEFAULT_DATA);
  const [loading, setLoading] = useState(!!userId);
  const [devDate, setDevDate] = useState(getInitialDevDate);
  const [selectedPastDay, setSelectedPastDay] = useState<DateStr | null>(null);

  // Initial load: pulls from localStorage immediately, then reconciles with
  // the API if a userId is present (see persistence/sync.ts).
  useEffect(() => {
    let cancelled = false;
    loadState().then((loaded) => {
      if (!cancelled) {
        setData((prev) => (prev === DEFAULT_DATA ? loaded : prev));
        setLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const now: Date = DEV_MODE && devDate ? devDate : new Date();
  const today = todayStr(now);
  const yesterday = yesterdayStr(now);

  const checkinSet = useMemo(() => new Set(data.checkins), [data.checkins]);
  const checkedInToday = checkinSet.has(today);
  const streak = useMemo(
    () => calcStreak(data.checkins, now),
    [data.checkins, today],
  );
  const previousStreak = useMemo(
    () => calcPreviousStreak(data.checkins, now),
    [data.checkins, today],
  );
  const longestStreak = data.longestStreak;
  const monthlyRate = useMemo(
    () => calcMonthlyRate(data.checkins, now),
    [data.checkins, today],
  );
  const weekDays = useMemo(
    () => getWeekDays(data.checkins, now),
    [data.checkins, today],
  );
  const nextMilestone = useMemo(() => getNextMilestone(streak), [streak]);
  const weekStripGoalText = nextMilestone
    ? `${nextMilestone.days - streak} to ${nextMilestone.icon} ${nextMilestone.name}`
    : "♛ Elite";
  const tipIndex = getDayOfYear(now) % 7;
  const tip = CREATINE_TIPS[tipIndex];

  const currentScreen = useMemo(() => {
    if (data.isFirstVisit && data.checkins.length === 0) return 4;
    if (checkedInToday) return 2;
    if (
      data.checkins.length > 0 &&
      !checkinSet.has(yesterday) &&
      !checkedInToday
    )
      return 3;
    return 1;
  }, [data, checkedInToday, checkinSet, yesterday]);

  useEffect(() => {
    if (currentScreen !== 3) return;
    const key = "nn-mixpanel-streak-broken-fired";
    if (localStorage.getItem(key) === today) return;
    localStorage.setItem(key, today);
    track("APP_STREAK_BROKEN", {
      supplement: SUPPLEMENT,
      longestStreak: data.longestStreak,
    });
  }, [currentScreen, today, data.longestStreak]);

  const handleCheckin = useCallback(
    (targetDate?: DateStr) => {
      const freshNow: Date = DEV_MODE && devDate ? devDate : new Date();
      const freshToday = todayStr(freshNow);
      const target = targetDate ?? freshToday;
      const isBackfill = target !== freshToday;
      if (data.checkins.includes(target)) return;
      const newCheckins = [...data.checkins, target];
      const newStreak = calcStreak(newCheckins, freshNow);
      const newLongest = Math.max(data.longestStreak, newStreak);
      const newFirstCheckinDate = data.firstCheckinDate
        ? target < data.firstCheckinDate
          ? target
          : data.firstCheckinDate
        : target;
      const newData: StreakData = {
        ...data,
        checkins: newCheckins,
        longestStreak: newLongest,
        isFirstVisit: false,
        firstCheckinDate: newFirstCheckinDate,
      };
      const wasStreakBroken = currentScreen === 3;
      setData(newData);
      setSelectedPastDay(null);
      recordCheckin(newData, target);
      if (wasStreakBroken && !isBackfill) {
        track("APP_NEW_STREAK_STARTED", {
          supplement: SUPPLEMENT,
          previousLongest: data.longestStreak,
        });
      }
      track("APP_DAILY_CHECKIN_COMPLETED", {
        supplement: SUPPLEMENT,
        day: newStreak,
        backfilled: isBackfill,
      });
      for (const m of MILESTONES) {
        if (newStreak === m.days) {
          track("APP_MILESTONE_ACHIEVED", {
            supplement: SUPPLEMENT,
            day: m.days,
          });
        }
      }
    },
    [data, devDate, currentScreen],
  );

  const handleSelectPastDay = useCallback((date: DateStr) => {
    setSelectedPastDay((prev) => (prev === date ? null : date));
  }, []);

  const handleStartTracking = useCallback(() => {
    const newData: StreakData = { ...data, isFirstVisit: false };
    setData(newData);
    markStartedTracking(newData);
    track("APP_ONBOARDING_STARTED", { supplement: SUPPLEMENT });
  }, [data]);

  const updateDevDateParam = useCallback((d: Date) => {
    const url = new URL(window.location.href);
    url.searchParams.set("date", dateStr(d));
    window.history.replaceState({}, "", url);
  }, []);

  const handleNextDay = useCallback(() => {
    if (!devDate) return;
    const next = new Date(devDate);
    next.setDate(next.getDate() + 1);
    setDevDate(next);
    updateDevDateParam(next);
  }, [devDate, updateDevDateParam]);

  const handleResetData = useCallback(() => {
    const newData: StreakData = { ...DEFAULT_DATA };
    setData(newData);
    resetAll(newData);
    localStorage.removeItem("nn-mixpanel-streak-broken-fired");
    const initial = getInitialDevDate();
    setDevDate(initial);
    const url = new URL(window.location.href);
    url.searchParams.delete("date");
    window.history.replaceState({}, "", url);
  }, []);

  // ── Render Helpers ─────────────────────────────────────────────

  // Header commented out — Fuego app shell provides navigation
  // function renderHeader() {
  //   const isBlue = currentScreen === 2;
  //   return (
  //     <div style={{ ...s.header, ...(isBlue ? { background: '#0441BE', borderBottom: '1px solid rgba(255,255,255,0.1)' } : {}) }}>
  //       <div style={s.headerLeft}>
  //         <button style={{ ...s.backArrow, color: isBlue ? '#FFF' : '#1A1A1A' }} aria-label="Back">‹</button>
  //         <span style={{ ...s.headerTitle, color: isBlue ? '#FFF' : '#1A1A1A' }}>Streak Tracker</span>
  //       </div>
  //       <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
  //         <circle cx="10" cy="10" r="8.5" stroke={isBlue ? '#FFF' : '#1A1A1A'} strokeWidth="1.5" />
  //         <path d="M10 6v4l2.5 1.5" stroke={isBlue ? '#FFF' : '#1A1A1A'} strokeWidth="1.5" strokeLinecap="round" />
  //       </svg>
  //     </div>
  //   );
  // }

  function renderDevBar() {
    if (!DEV_MODE) return null;
    return (
      <div style={s.devBar}>
        <span style={s.devLabel}>DEV</span>
        <span style={s.devDate}>{today}</span>
        <span style={s.devScreen}>Screen {currentScreen}</span>
        <button style={s.devBtn} onClick={handleNextDay}>
          Next Day →
        </button>
        <button
          style={{ ...s.devBtn, ...s.devBtnDanger }}
          onClick={handleResetData}
        >
          Reset
        </button>
      </div>
    );
  }

  function renderHero() {
    let heroStyle = { ...s.hero };
    let countStyle = { ...s.heroCount };
    let productStyle = { ...s.heroProduct };
    let messageStyle = { ...s.heroMessage };
    let productLabel = "NAKED CREATINE";
    let message = "";

    if (currentScreen === 1) {
      if (nextMilestone) {
        const remaining = nextMilestone.days - streak;
        message = `${remaining} more day${remaining !== 1 ? "s" : ""} to hit ${nextMilestone.name}. Keep building.`;
      } else {
        message = "All milestones achieved. Legendary consistency.";
      }
    } else if (currentScreen === 2) {
      heroStyle = {
        ...heroStyle,
        background: "linear-gradient(135deg, #0441BE 0%, #0336A0 100%)",
      };
      productStyle = { ...productStyle, color: "rgba(255,255,255,0.6)" };
      messageStyle = { ...messageStyle, color: "rgba(255,255,255,0.85)" };
      if (nextMilestone) {
        const remaining = nextMilestone.days - streak;
        message = `${remaining} day${remaining !== 1 ? "s" : ""} to ${nextMilestone.name}. Real momentum building.`;
      } else {
        message = "All milestones achieved. Legendary consistency.";
      }
    } else if (currentScreen === 3) {
      heroStyle = { ...heroStyle, background: "#1A1A1A" };
      countStyle = { ...countStyle, color: "rgba(255,255,255,0.4)" };
      productStyle = { ...productStyle, color: "#999" };
      messageStyle = { ...messageStyle, color: "rgba(255,255,255,0.55)" };
      message = "Streaks reset, but your gains don't. Pick it back up.";
    } else if (currentScreen === 4) {
      countStyle = { ...countStyle, color: "rgba(255,255,255,0.25)" };
      productLabel = "Your Streak Starts Today";
      message = "Track daily supplements. Build consistency.";
    }

    return (
      <div style={heroStyle}>
        <div style={s.heroGlow} />
        <div style={s.heroNumberBlock}>
          <div style={countStyle}>{streak}</div>
          <div style={s.heroUnit}>Days</div>
        </div>
        <div style={s.heroInfo}>
          <div style={productStyle}>{productLabel}</div>
          <div style={messageStyle}>{message}</div>
        </div>
      </div>
    );
  }

  function renderStatsRow() {
    return (
      <div style={s.statsRow}>
        <div style={s.statItem}>
          <div style={s.statValue}>{streak}</div>
          <div style={s.statLabel}>Current</div>
        </div>
        <div style={s.statItem}>
          <div style={s.statValue}>{longestStreak}</div>
          <div style={s.statLabel}>Longest</div>
        </div>
        <div style={s.statItemLast}>
          <div style={s.statValue}>
            {monthlyRate !== null ? `${monthlyRate}%` : "—"}
          </div>
          <div style={s.statLabel}>This Month</div>
        </div>
      </div>
    );
  }

  function renderWelcomeCard() {
    return (
      <div style={s.welcomeCard}>
        <div style={s.wbTitle}>Welcome back</div>
        <div style={s.wbDesc}>
          Creatine levels stay elevated for days after a missed dose. One
          check-in and you're building again.
        </div>
        <div style={s.wbPrevStreak}>Previous streak: {previousStreak} days</div>
      </div>
    );
  }

  function renderTipCard() {
    return (
      <div style={s.tipCard}>
        <div style={s.tipHeader}>
          <span style={s.tipLabel}>Did You Know</span>
          <span style={s.tipCounter}>{tipIndex + 1} of 7</span>
        </div>
        <div style={s.tipText}>{tip.text}</div>
        <div style={s.tipSource}>{tip.source}</div>
      </div>
    );
  }

  function renderMilestones() {
    return (
      <div style={s.milestonesSection}>
        <div style={s.sectionTitleSm}>Milestones</div>
        <div style={s.milestoneTrack}>
          {MILESTONES.map((m) => {
            const achieved = streak >= m.days;
            const isNext =
              !achieved && MILESTONES.find((ms) => ms.days > streak) === m;
            let itemStyle = { ...s.milestone };
            if (achieved) itemStyle = { ...itemStyle, ...s.msAchieved };
            else if (isNext) itemStyle = { ...itemStyle, ...s.msNext };

            return (
              <div key={m.days} style={itemStyle}>
                <div style={s.msIcon}>{m.icon}</div>
                <div style={{ ...s.msDays, ...(isNext ? s.msDaysBlue : {}) }}>
                  {m.days}
                </div>
                <div
                  style={{
                    ...s.msLabel,
                    ...(achieved ? s.msLabelAchieved : {}),
                  }}
                >
                  {m.name}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function renderProgressBar() {
    if (!nextMilestone) return null;
    const pct = Math.min(100, Math.round((streak / nextMilestone.days) * 100));
    return (
      <div style={s.progressBar}>
        <div style={s.mpBarWrap}>
          <div style={s.mpBar}>
            <div style={{ ...s.mpBarFill, width: `${pct}%` }} />
          </div>
          <span style={s.mpBarLabel}>
            {streak} of {nextMilestone.days} days
          </span>
        </div>
      </div>
    );
  }

  function renderOnboarding() {
    return (
      <div style={s.onboardingContent}>
        <div style={s.onboardingTitle}>Consistency is the protocol.</div>
        <div style={s.onboardingDesc}>
          Creatine works through daily saturation — not single doses. Track your
          intake, build a streak, and see results compound over time.
        </div>
        <div style={s.onboardingSteps}>
          <div style={s.onboardingStep}>
            <div style={s.stepNum}>1</div>
            <div style={s.stepText}>
              <strong style={{ color: "#1A1A1A" }}>Check in daily</strong> — one
              tap to log your creatine and other Naked supplements
            </div>
          </div>
          <div style={s.onboardingStep}>
            <div style={s.stepNum}>2</div>
            <div style={s.stepText}>
              <strong style={{ color: "#1A1A1A" }}>Build your streak</strong> —
              watch your consistency grow day by day
            </div>
          </div>
          <div style={s.onboardingStep}>
            <div style={s.stepNum}>3</div>
            <div style={s.stepText}>
              <strong style={{ color: "#1A1A1A" }}>Hit milestones</strong> —
              Loaded at 7 days, Saturated at 30, Elite at 90
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderCTA() {
    let btnStyle: CSSProperties = { ...s.ctaBtn };
    let btnText = "";
    let disabled = false;
    let onClick: (() => void) | undefined = () => handleCheckin();

    if (selectedPastDay) {
      btnStyle = { ...btnStyle, ...s.ctaStart };
      btnText = `Check in for ${formatDateLabel(selectedPastDay)}`;
      onClick = () => handleCheckin(selectedPastDay);
    } else if (currentScreen === 1) {
      btnText = "○  I took my creatine today";
    } else if (currentScreen === 2) {
      btnStyle = { ...btnStyle, ...s.ctaDone };
      btnText = `✓  Day ${streak}`;
      disabled = true;
      onClick = undefined;
    } else if (currentScreen === 3) {
      btnStyle = { ...btnStyle, ...s.ctaStart };
      btnText = "Start a new streak";
    } else if (currentScreen === 4) {
      btnStyle = { ...btnStyle, ...s.ctaStart };
      btnText = "Start tracking";
      onClick = handleStartTracking;
    }

    return (
      <div style={s.stickyFooter}>
        <button style={btnStyle} disabled={disabled} onClick={onClick}>
          {btnText}
        </button>
        <div style={s.fda}>
          *These statements have not been evaluated by the FDA. This product is
          not intended to diagnose, treat, cure, or prevent any disease.
        </div>
      </div>
    );
  }

  // ── Main Render ────────────────────────────────────────────────

  if (loading) return <StreakSkeleton layout={s as SkeletonLayout} />;

  return (
    <div style={s.wrap}>
      {renderDevBar()}
      {renderHero()}
      {currentScreen !== 4 && renderStatsRow()}
      <div style={s.content}>
        {currentScreen === 3 && renderWelcomeCard()}
        {currentScreen !== 4 && (
          <WeekStrip
            weekDays={weekDays}
            selectedDate={selectedPastDay}
            onSelectDay={handleSelectPastDay}
            goalText={weekStripGoalText}
            styles={s as WeekStripStyles}
          />
        )}
        {currentScreen === 4 ? (
          renderOnboarding()
        ) : (
          <>
            {renderTipCard()}
            {renderMilestones()}
            {renderProgressBar()}
          </>
        )}
      </div>
      {renderCTA()}
    </div>
  );
}
